// ===== Learning Configuration v2 Types =====
// Design spec: docs/superpowers/specs/2026-04-08-learning-configuration-renewal-design.md

// --- Hierarchy Level ---
export type HierarchyLevel = 'product' | 'curriculum' | 'course' | 'module'

// --- Component Types ---
export type ComponentTypeV2 = 'quiz' | 'video' | 'toon' | 'assignment' | 'interactive' | 'guide' | 'buildup' | 'warmup'

export type QuizPurpose = 'selected' | 'review' | 'block' | 'checkup' | 'applied' | 'wrapup' | 'aiq'

// --- Root Configuration ---
export interface LearningConfigurationV2 {
  version: '2.0'
  product: BranchableLevelConfig
  curriculum: BranchableLevelConfig
  course: BranchableLevelConfig
  module: BranchableLevelConfig
  lesson: BranchableLessonConfig
  completionRules: CompletionRulesV2
  learningSettings: LearningSettingsV2
}

// --- Level Configs ---
export interface LevelConfig {
  pre: ComponentConfigV2[]
  post: ComponentConfigV2[]
}

// G9 — 오프라인 교재(체크업북/워크북) 유무 토글. 2 flags(축3=A) — 트랙별 비대칭 표현.
// 필드 누락 시 노출(true, 엣지1 비파괴). course 레벨만 authoritative-read(엣지6).
export type LearnerNoticeMaterialType = 'CHECKUP_BOOK' | 'WORKBOOK'

export interface OfflineMaterials {
  checkupBook: boolean
  workbook: boolean
  /** 깨처 교재 펼침 팝업의 단일 교재 유형. 메인 교재 목록과는 별개다. */
  learnerNoticeMaterialType?: LearnerNoticeMaterialType
}

export interface BranchableLevelConfig {
  belongsTo: HierarchyLevel | null
  default: LevelConfig
  overrides: BranchOverride[]
  /** G9 — course 레벨 오프라인 교재 토글(다른 레벨엔 존재해도 무시). */
  offlineMaterials?: OfflineMaterials
}

export interface BranchOverride {
  contentId: number
  contentName: string
  pre?: ComponentConfigV2[]
  post?: ComponentConfigV2[]
}

export interface BranchableLessonConfig {
  belongsTo: HierarchyLevel | null
  default: LessonLevelConfig
  overrides: LessonBranchOverride[]
  /** G9 — 레슨 섹션 레벨 오프라인 교재 토글 override(course 기본을 이김). */
  offlineMaterials?: OfflineMaterials
}

export interface LessonLevelConfig {
  components: ComponentConfigV2[]
}

export interface LessonBranchOverride {
  contentId: number
  contentName: string
  components?: ComponentConfigV2[]
}

// --- Component Config ---
export interface ComponentConfigV2 {
  id: string
  type: ComponentTypeV2
  enabled: boolean
  label?: string
  labelEn?: string
  sortOrder: number
  /** 1회차에만 제공(복습 회차에서 제외). 기본 false = 비파괴. */
  firstAttemptOnly?: boolean
  /**
   * 생각열기(warmup) 전용 회차별 정책. 다른 타입에는 저장하지 않는다(백엔드 WarmupPolicyValidator 가 거부).
   * 누락 = 기존 enabled/firstAttemptOnly/audioResponse 해석 유지(비파괴). 조회·편집 진입만으로 만들지 않는다.
   * Spec: docs/superpowers/specs/2026-09-09-lc-review-warmup-policy-design.md §3
   */
  warmupPolicy?: WarmupPolicy
  options: QuizOptions | VideoOptions | ToonOptions | AssignmentOptions | InteractiveOptions | GuideOptions | BuildupOptions | WarmupOptions
}

/** 생각열기 회차별 제공 방식. hidden=미제공, required=필수, optional=선택(녹음 없이 건너뛰기 가능). */
export type WarmupPolicyMode = 'hidden' | 'required' | 'optional'

export interface WarmupPolicy {
  /** 오늘 수업 최초 진도 */
  initial: WarmupPolicyMode
  /** 오늘 수업 지정 복습 */
  designatedReview: WarmupPolicyMode
}

// --- Quiz Options ---
export interface QuizOptions {
  extraction: {
    contentScope: ('current' | 'previous' | 'next')[]
    quizLocations: QuizPurpose[]
    /**
     * G5 — 'wrong_in_review' = 오답 풀기(이번 복습 회차 오답). 복습(DESIGNATED_REVIEW) 레슨에서
     * 이번 회차 오답 ∩ 비-block 후보만 출제. 백엔드가 해설을 always 강제하고 오답 0개면 학습자앱이
     * 자동 스킵한다. 백엔드 config-v2 DTO 는 answerFilter 를 generic String 으로 읽으므로 백엔드 타입 변경 불필요.
     */
    answerFilter: 'all' | 'incorrect' | 'wrong_in_review'
  }
  count: {
    value: number | 'max'
    shortageStrategy?: {
      action: 'available_only' | 'fill'
      fillMethod?: 'from_correct' | 'from_previous_incorrect'
    }
  }
  ordering: 'random' | 'sequential'
  useFixedVars: boolean
  solveMode: 'lesson' | 'test'
  showHint: boolean
  /** G3 — 오답 해설 노출 정책. 'always'(기본=해설 노출) | 'hide_on_incorrect'(오답 시 해설 숨김). */
  answerExplanationPolicy?: 'always' | 'hide_on_incorrect'
  /**
   * G4 — 선택지 텍스트 흐리기 정책. 'show'(기본=정상 노출) | 'dimmed'(선택지 텍스트 opacity 0.05).
   * 1회차(책 풀이) 한정 발동. multi_choice·inline 만 반응(ordering·pairing·drag_text·select_text는 노출 유지).
   * 누락=show(비파괴).
   */
  choiceDisplayPolicy?: 'show' | 'dimmed'
  /**
   * G8 — 1회차 "교재 풀이 → 패드 입력(채점 보류)" 단계 여부. true 면 학습자앱이 자동채점 대신
   * 오프라인 입력 패드를 노출(체크업북 트랙·1회차·비복습 충족 시). 채점은 강사가 코칭 콘솔에서.
   */
  offlineInput?: boolean
  completion: {
    passingScore: number
    maxAttempts: number
  }
}

// --- Video Options ---
export type VideoSource =
  | { type: 'lesson'; contentScope: 'current' | 'previous' | 'next'; subtype: 'main' | 'flash' }
  | { type: 'library'; resourceId: number }
  | { type: 'url'; url: string }

export interface VideoOptions {
  pauseEnabled: boolean
  source: VideoSource
  skipPolicy: 'always' | 'after_first_watch'
  speedPolicy: 'always' | 'after_first_watch'
  requiredWatchPercent: number
  // Video Phase B — 1회차 Pause 모드
  pauseFirstWatchMode: 'guide' | 'solve'
  // G2 — 영상 자동 재생 (마운트 시 바로 재생). 기본 false.
  autoPlay: boolean
}

// --- Toon Options ---
export interface ToonOptions {
  toonType: 'intro' | 'outro'
}

// --- Assignment Options ---
export interface AssignmentOptions {
  diaryQuizSetId?: number
}

// --- Interactive Options ---
export type InteractiveKind = 'tool' | 'game'

export interface InteractiveOptions {
  kind: InteractiveKind
}

// --- Guide Options ---
export interface GuideOptions {
  templateId: string | null
  showMessage: boolean
  message?: string
  messageEn?: string
  audioResponse: {
    enabled: boolean
    required?: boolean
  }
}

// --- Buildup Options ---
// 빌드업 본문은 queture_contents.buildup에서 오며, 음성 응답의 자동완료 필수 여부만 설정한다.
export interface BuildupOptions {
  audioResponse?: {
    enabled: boolean
    required: boolean
  }
}

export function createDefaultBuildupOptions(): BuildupOptions {
  return { audioResponse: { enabled: true, required: false } }
}

// --- Warmup Options ---
// 생각열기/포인트 콘텐츠는 queture_contents.warmupText/shortPoint 에서 자동 노출.
// 옵션은 음성 응답(enabled/required)만.
export interface WarmupOptions {
  audioResponse: {
    enabled: boolean
    required: boolean
  }
}

export function createDefaultWarmupOptions(): WarmupOptions {
  return { audioResponse: { enabled: true, required: false } }
}

// --- Completion Rules V2 ---
export interface CompletionRulesV2 {
  enforceSequentialProgress: boolean
  lessonSequenceMode: 'sequential' | 'flexible'
  allRequiredMustComplete: boolean
  levelCompletion?: {
    product?: { minimumScore?: number }
    curriculum?: { minimumScore?: number }
    course?: { minimumScore?: number }
    module?: { minimumScore?: number }
    lesson?: { minimumScore?: number }
  }
}

// --- Learning Settings V2 ---
export interface LearningSettingsV2 {
  showDashboard: boolean
  showProgress: boolean
  trackLearningTime: boolean
  enableNotes: boolean
  enableBookmarks: boolean
  showTimer: boolean
  allowSkip: boolean
}

// --- API DTOs ---
export type ContentHierarchyLevel = 'program' | 'curriculum' | 'course' | 'module' | 'lesson'

export interface ContentPathItemDto {
  contentId: number
  contentName: string
  hierarchyLevel: ContentHierarchyLevel
}

export interface ContentLearningConfigurationDto {
  contentId: number
  contentName: string | null
  contentHierarchyLevel: ContentHierarchyLevel | null
  contentPath: ContentPathItemDto[]
  learningConfigurationId: number
  templateName: string | null
}

export interface EffectiveConfigurationV2Response {
  configuration: LearningConfigurationV2
  version: string
  source: 'content_default' | 'single_template' | 'per_hierarchy' | 'v1_fallback'
}

// --- Factory Functions ---

export function createDefaultQuizOptions(): QuizOptions {
  return {
    extraction: {
      contentScope: ['current'],
      quizLocations: ['selected'],
      answerFilter: 'all',
    },
    count: { value: 5 },
    ordering: 'sequential',
    useFixedVars: false,
    solveMode: 'lesson',
    showHint: false,
    answerExplanationPolicy: 'always',
    choiceDisplayPolicy: 'show',
    completion: { passingScore: 70, maxAttempts: 3 },
  }
}

export function createDefaultVideoOptions(): VideoOptions {
  return {
    pauseEnabled: false,
    source: { type: 'lesson', contentScope: 'current', subtype: 'main' },
    skipPolicy: 'after_first_watch',
    speedPolicy: 'after_first_watch',
    requiredWatchPercent: 90,
    pauseFirstWatchMode: 'guide',
    autoPlay: false,
  }
}

export function createDefaultLearningConfigurationV2(): LearningConfigurationV2 {
  const emptyLevel: BranchableLevelConfig = {
    belongsTo: null,
    default: { pre: [], post: [] },
    overrides: [],
  }
  return {
    version: '2.0',
    product: { ...emptyLevel },
    curriculum: { ...emptyLevel },
    course: { ...emptyLevel },
    module: { ...emptyLevel },
    lesson: {
      belongsTo: null,
      default: { components: [] },
      overrides: [],
    },
    completionRules: {
      enforceSequentialProgress: true,
      lessonSequenceMode: 'sequential',
      allRequiredMustComplete: true,
    },
    learningSettings: {
      showDashboard: true,
      showProgress: true,
      trackLearningTime: true,
      enableNotes: false,
      enableBookmarks: false,
      showTimer: false,
      allowSkip: false,
    },
  }
}

// --- Component Display Info ---
export const COMPONENT_TYPE_INFO: Record<ComponentTypeV2, { icon: string; label: string; color: string }> = {
  quiz: { icon: 'Q', label: 'Quiz', color: '#6c5ce7' },
  video: { icon: '▶', label: 'Video', color: '#e17055' },
  toon: { icon: 'T', label: 'Toon', color: '#fdcb6e' },
  assignment: { icon: '📝', label: 'Assignment', color: '#a29bfe' },
  interactive: { icon: '🎮', label: 'Interactive', color: '#00b894' },
  guide: { icon: '📢', label: 'Guide', color: '#74b9ff' },
  buildup: { icon: 'B', label: 'Buildup', color: '#fd79a8' },
  warmup: { icon: '💡', label: 'Warmup', color: '#f6b93b' },
}
